import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { KiEntwurfAngelegt, KiEntwurfStand, KiEntwurfUebernahme, KiTestfallEntwurf } from '../../../models/ki.model';
import { KiEntwurfService } from '../../../services/ki-entwurf.service';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { KiBadgeComponent } from '../ki-badge/ki-badge.component';
import { KiFehlerComponent } from '../ki-fehler/ki-fehler.component';
import { fokusImBereichHalten } from '../ki-fokus';

/**
 * Eine Karte je Testfall-Entwurf im Panel „Testfall-Entwürfe“ (page-ki.jsx:283-313). Den Stand hält der Aufrufer
 * (`[(stand)]`), damit er offene Entwürfe zählen kann; eine neue Anlage meldet `angelegt`. Bearbeitbar ist nur der
 * Titel (page-ki.jsx:286-288); Vorbedingung und Schritte ändert man nach der Übernahme im Testfall.
 */
@Component({
  selector: 'app-ki-entwurf-card',
  imports: [KiBadgeComponent, KiFehlerComponent, MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  templateUrl: './ki-entwurf-card.component.html',
  styleUrl: './ki-entwurf-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KiEntwurfCardComponent {
  private readonly entwuerfe = inject(KiEntwurfService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly entwurf = input.required<KiTestfallEntwurf>();
  readonly projectId = input.required<string>();
  readonly requirementId = input.required<string>();
  /** Anfrage, aus deren gespeichertem Ergebnis das Backend den Entwurf übernimmt. */
  readonly anfrageId = input.required<string>();
  /** Fachliche ID der Anforderung für „Übernommen · mit {AnfID} verknüpft“. */
  readonly anfId = input.required<string>();
  readonly stand = model<KiEntwurfStand>('offen');
  readonly angelegt = output<KiEntwurfAngelegt>();

  protected readonly maxLaenge = FIELD_LENGTHS.NAME_MAX;
  protected readonly sendet = signal(false);
  protected readonly fehlerKey = signal<string | null>(null);
  /** Nach 403: ohne Schreibrecht hilft kein neuer Versuch, die Übernahme bleibt gesperrt. */
  protected readonly gesperrt = signal(false);
  protected readonly bearbeiten = signal(false);
  protected readonly titelEntwurf = signal('');
  /** Der beim Übernehmen geänderte Titel; bis dahin gilt der Titel des Entwurfs. */
  private readonly geaenderterTitel = signal<string | null>(null);
  private readonly titelEingabe = viewChild<ElementRef<HTMLInputElement>>('titelEingabe');
  private readonly karte = viewChild.required<ElementRef<HTMLElement>>('karte');
  private readonly titelText = viewChild<ElementRef<HTMLElement>>('titelText');
  /** Nur ein abgeschnittener Titel bekommt Tooltip und Tab-Stopp; sonst stünde dort, was schon zu lesen ist. */
  protected readonly gekuerzt = signal(false);

  protected readonly titel = computed(() => this.geaenderterTitel() ?? this.entwurf().titel ?? '');
  protected readonly prioritaetHoch = computed(() => {
    const prioritaet = this.entwurf().prioritaet;
    return prioritaet === 'CRITICAL' || prioritaet === 'HIGH';
  });
  /**
   * Einen zu langen KI-Titel kürzt das Backend; die Länge zählt nur für den eigenen Titel. `maxlength` greift nur
   * beim Tippen, ein per Skript gesetzter Wert kann länger sein.
   */
  protected readonly kannUebernehmen = computed(() => {
    if (this.gesperrt()) return false;
    if (!this.bearbeiten()) return this.titel().trim().length > 0;
    const titel = this.titelEntwurf();
    return titel.trim().length > 0 && titel.length <= this.maxLaenge;
  });

  constructor() {
    effect(() => this.titelEingabe()?.nativeElement.focus());
    afterRenderEffect((onCleanup) => {
      this.titel();
      const element = this.titelText()?.nativeElement;
      if (!element) {
        this.gekuerzt.set(false);
        return;
      }
      const messen = (): void => this.gekuerzt.set(element.scrollWidth > element.clientWidth);
      messen();
      const beobachter = new ResizeObserver(messen);
      beobachter.observe(element);
      onCleanup(() => beobachter.disconnect());
    });
  }

  protected startBearbeiten(): void {
    this.titelEntwurf.set(this.titel());
    this.bearbeiten.set(true);
  }

  protected abbrechen(): void {
    this.fokusInKarteHalten();
    this.bearbeiten.set(false);
  }

  protected verwerfen(): void {
    this.fokusInKarteHalten();
    this.bearbeiten.set(false);
    this.fehlerKey.set(null);
    this.stand.set('verworfen');
  }

  protected rueckgaengig(): void {
    this.fokusInKarteHalten();
    this.stand.set('offen');
  }

  protected titelGeaendert(event: Event): void {
    this.titelEntwurf.set((event.target as HTMLInputElement).value);
  }

  /** Die Karte liegt im Formular der Anforderung: Enter darf es nicht absenden. */
  protected enter(event: Event): void {
    event.preventDefault();
    this.uebernehmen();
  }

  protected escape(event: Event): void {
    event.stopPropagation();
    this.abbrechen();
  }

  protected uebernehmen(): void {
    if (this.sendet() || !this.kannUebernehmen()) return;
    const entwurf = this.entwurf();
    const titel = this.bearbeiten() ? this.titelEntwurf().trim() : undefined;
    this.fokusInKarteHalten();
    this.sendet.set(true);
    this.fehlerKey.set(null);
    this.entwuerfe
      .uebernehmen(this.projectId(), this.requirementId(), entwurf.entwurfId, this.anfrageId(), titel)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ergebnis) => {
        this.sendet.set(false);
        this.abschliessen(ergebnis, entwurf, titel);
      });
  }

  private abschliessen(ergebnis: KiEntwurfUebernahme, entwurf: KiTestfallEntwurf, titel?: string): void {
    this.fokusInKarteHalten();
    if (ergebnis.art === 'fehler') {
      this.fehlerKey.set(ergebnis.textKey);
      this.gesperrt.set(!ergebnis.erneutMoeglich);
      return;
    }
    if (titel !== undefined) this.geaenderterTitel.set(titel);
    this.bearbeiten.set(false);
    this.stand.set('uebernommen');
    if (ergebnis.art === 'angelegt') {
      this.angelegt.emit({
        entwurfId: entwurf.entwurfId,
        testCaseId: ergebnis.testCaseId,
        key: ergebnis.key,
        titel: this.titel(),
      });
    }
  }

  /** Nimmt eine Aktion ihren Knopf mit, geht der Fokus auf den ersten bedienbaren Knopf im Fuß, sonst auf die Karte. */
  private fokusInKarteHalten(): void {
    const karte = this.karte().nativeElement;
    fokusImBereichHalten(karte, this.injector, () =>
      (karte.querySelector<HTMLElement>('.ki-entwurf-card__fuss button:not([disabled])') ?? karte).focus(),
    );
  }
}
